import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User } from 'firebase/auth';
import {
  followUser as functionsFollowUser,
  getFollowStatus,
  SocialActionResult,
  FollowStatusResult,
  shouldUseFunctionsAPI,
  sendFollowRequest as apiSendFollowRequest,
  cancelFollowRequest as apiCancelFollowRequest,
  acceptFollowRequest as apiAcceptFollowRequest,
  declineFollowRequest as apiDeclineFollowRequest,
  hasFollowRequest as apiHasFollowRequest,
} from '../services/functionsApi';
import { socialEventBroadcaster, SocialEventData } from '../services/socialEventBroadcaster';
import { UserActivity } from '@watchlist/shared';
import { socialService } from '../services/socialService';
import { privacyService } from '../services/privacyService';
import { isFirebaseInitialized } from '@watchlist/shared';
import { isCapacitorNative, getFirestoreQueryTimeout } from '../utils/platformUtils';
import { hapticLight } from '../utils/nativeHaptics';

export interface UserFollowStats {
  followersCount: number;
  followingCount: number;
  followedUsers: string[];
  followers: string[];
  lastUpdated?: Date;
}

interface UserFollowContextType {
  // State
  followStats: UserFollowStats;
  followingActivity: UserActivity[];
  isLoading: boolean;
  error: string | null;

  // Pending follow requests sent by the current user (Set of targetUids)
  pendingRequests: Set<string>;

  // User following actions - now using atomic Functions
  followUser: (targetUserId: string) => Promise<void>;
  unfollowUser: (targetUserId: string) => Promise<void>;
  isFollowing: (targetUserId: string) => Promise<boolean>;

  // Follow-request actions (for private profiles)
  sendFollowRequest: (targetUserId: string) => Promise<void>;
  cancelFollowRequest: (targetUserId: string) => Promise<void>;
  acceptFollowRequest: (requesterUserId: string) => Promise<void>;
  declineFollowRequest: (requesterUserId: string) => Promise<void>;
  isRequestPending: (targetUserId: string) => boolean;

  // Data fetching
  refreshFollowData: () => Promise<void>;
  getFollowingActivity: () => Promise<void>;
  getUserFollowers: (userId: string) => Promise<string[]>;
  getUserFollowing: (userId: string) => Promise<string[]>;

  // Helper methods
  isUserFollowed: (userId: string) => boolean;
  refreshUserFollowState: (userId: string) => Promise<boolean>;
}

const UserFollowContext = createContext<UserFollowContextType | undefined>(undefined);

interface UserFollowProviderProps {
  children: ReactNode;
  user?: User | null;
}

export const UserFollowProvider: React.FC<UserFollowProviderProps> = ({
  children,
  user
}) => {
  const [followStats, setFollowStats] = useState<UserFollowStats>({
    followersCount: 0,
    followingCount: 0,
    followedUsers: [],
    followers: [],
    lastUpdated: new Date()
  });

  const [followingActivity, setFollowingActivity] = useState<UserActivity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingRequests, setPendingRequests] = useState<Set<string>>(new Set());

  // Refresh follow data from Firebase
  const refreshFollowData = useCallback(async () => {
    if (!user) {
      // Reset state for non-authenticated users
      setFollowStats({
        followersCount: 0,
        followingCount: 0,
        followedUsers: [],
        followers: [],
        lastUpdated: new Date()
      });
      setFollowingActivity([]);
      return;
    }

    // Check if Firebase is properly initialized before attempting operations
    // Return early if not - will retry when user state updates
    if (!isFirebaseInitialized()) {
      console.log('UserFollowContext: Firebase not yet initialized, will retry when user state updates');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('UserFollowContext: Refreshing follow data for user:', user.uid);

      // Add timeout for native platforms (Firestore queries can be slower on iOS)
      // Using centralized platform detection for consistency
      const followQueryTimeout = getFirestoreQueryTimeout();
      console.log(`UserFollowContext: Using ${followQueryTimeout}ms timeout for follow data (native: ${isCapacitorNative()})`);

      // Load following and followers data with timeout (clear timer when queries win to avoid false "timeout" logs)
      let followRaceTimer: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<[string[], string[]]>((resolve) => {
        followRaceTimer = setTimeout(() => {
          console.warn(`UserFollowContext: Follow data query timeout after ${followQueryTimeout / 1000} seconds, returning empty data`);
          resolve([[], []]);
        }, followQueryTimeout);
      });

      let followingIds: string[];
      let followerIds: string[];
      try {
        [followingIds, followerIds] = await Promise.race([
          Promise.all([
            socialService.getFollowing(user.uid),
            socialService.getFollowers(user.uid)
          ]),
          timeoutPromise
        ]);
      } finally {
        if (followRaceTimer !== undefined) clearTimeout(followRaceTimer);
      }

      console.log('UserFollowContext: Fetched follow data:', {
        followingIds: followingIds.length,
        followerIds: followerIds.length
      });

      setFollowStats({
        followersCount: followerIds.length,
        followingCount: followingIds.length,
        followedUsers: followingIds,
        followers: followerIds,
        lastUpdated: new Date()
      });

      // Load activity from followed users only if we have followers
      if (followingIds.length > 0) {
        try {
          const activity = await socialService.getFollowingActivity(followingIds, 20);
          setFollowingActivity(activity);
        } catch (activityError) {
          console.warn('UserFollowContext: Failed to load activity, but continuing with follow data:', activityError);
          setFollowingActivity([]);
        }
      } else {
        setFollowingActivity([]);
      }
    } catch (error) {
      // Check if it's a permission error (Firestore security rules not configured)
      const isPermissionError = error instanceof Error && error.message.includes('permission');

      if (isPermissionError) {
        console.log('UserFollowContext: Follow feature disabled (Firestore permissions not configured)');
      } else {
        console.error('UserFollowContext: Error loading follow data:', error);
      }

      // Silently set empty state - follow features are optional
      setFollowStats({
        followersCount: 0,
        followingCount: 0,
        followedUsers: [],
        followers: [],
        lastUpdated: new Date()
      });
      setFollowingActivity([]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.uid]); // Only depend on user.uid, not the entire user object

  // Initialize follow data when user changes - but only once per user
  useEffect(() => {
    refreshFollowData();
  }, [refreshFollowData]);

  // Listen for social events to update follow state in real-time
  useEffect(() => {
    if (!user) return;

    const handleSocialEvent = (eventData: SocialEventData) => {
      // Only handle events relevant to this user
      if (eventData.userId !== user.uid && eventData.targetUserId !== user.uid) return;

      console.debug('UserFollowContext: social event', eventData.type, eventData.targetUserId);

      if (eventData.type === 'follow' && eventData.userId === user.uid && eventData.targetUserId) {
        // Current user followed someone (another tab/device, or broadcast after local followUser)
        const targetId = eventData.targetUserId;
        setFollowStats(prev => {
          if (prev.followedUsers.includes(targetId)) {
            return prev;
          }
          return {
            ...prev,
            followingCount: prev.followingCount + 1,
            followedUsers: Array.from(new Set([...prev.followedUsers, targetId])),
            lastUpdated: new Date()
          };
        });
      } else if (eventData.type === 'unfollow' && eventData.userId === user.uid && eventData.targetUserId) {
        const targetId = eventData.targetUserId;
        setFollowStats(prev => {
          if (!prev.followedUsers.includes(targetId)) {
            return prev;
          }
          return {
            ...prev,
            followingCount: Math.max(0, prev.followingCount - 1),
            followedUsers: prev.followedUsers.filter(id => id !== targetId),
            lastUpdated: new Date()
          };
        });
      } else if (eventData.type === 'follow' && eventData.targetUserId === user.uid && eventData.userId) {
        // Someone followed current user
        setFollowStats(prev => ({
          ...prev,
          followersCount: prev.followersCount + 1,
          followers: Array.from(new Set([...prev.followers, eventData.userId!])),
          lastUpdated: new Date()
        }));
      } else if (eventData.type === 'unfollow' && eventData.targetUserId === user.uid && eventData.userId) {
        // Someone unfollowed current user
        setFollowStats(prev => ({
          ...prev,
          followersCount: Math.max(0, prev.followersCount - 1),
          followers: prev.followers.filter(id => id !== eventData.userId),
          lastUpdated: new Date()
        }));
      }
    };

    // Subscribe to all social events
    const unsubscribe = socialEventBroadcaster.subscribe('*', handleSocialEvent);

    return () => {
      unsubscribe();
    };
  }, [user?.uid]);

  // 🚀 NEW: Atomic follow operation using Firebase Functions
  const followUser = useCallback(async (targetUserId: string) => {
    if (!user) {
      console.warn('User must be logged in to follow other users');
      throw new Error('User must be logged in to follow other users');
    }

    if (targetUserId === user.uid) {
      console.warn('User cannot follow themselves');
      throw new Error('You cannot follow yourself');
    }

    try {
      console.log('🚀 UserFollowContext: Using atomic Functions API to follow user:', targetUserId);

      // Atomic operation via Functions
      const result: SocialActionResult = await functionsFollowUser(targetUserId, 'follow');

      if (result.success) {
        console.log('✅ Successfully followed user via Functions:', result);

        // Update local state immediately with accurate counts from Functions
        setFollowStats(prev => ({
          ...prev,
          followingCount: result.followingCount || (prev.followingCount + 1),
          followedUsers: Array.from(new Set([...prev.followedUsers, targetUserId])),
          lastUpdated: new Date()
        }));

        // Broadcast follow event for real-time updates across all components
        socialEventBroadcaster.broadcastFollow(user.uid, targetUserId);

        console.log('✅ Local follow state updated immediately and event broadcasted');

        void hapticLight();

      } else {
        throw new Error(result.message || 'Follow operation failed');
      }

    } catch (error) {
      console.error('❌ Atomic follow operation failed:', error);

      // For debugging: log more details
      console.error('Follow error details:', {
        targetUserId,
        currentUser: user.uid,
        errorMessage: error instanceof Error ? error.message : String(error)
      });

      throw error; // Re-throw for component error handling
    }
  }, [user?.uid]);

  // 🚀 NEW: Atomic unfollow operation using Firebase Functions  
  const unfollowUser = useCallback(async (targetUserId: string) => {
    if (!user) {
      console.warn('User must be logged in to unfollow other users');
      throw new Error('User must be logged in to unfollow other users');
    }

    try {
      console.log('🚀 UserFollowContext: Using atomic Functions API to unfollow user:', targetUserId);

      // Atomic operation via Functions
      const result: SocialActionResult = await functionsFollowUser(targetUserId, 'unfollow');

      if (result.success) {
        console.log('✅ Successfully unfollowed user via Functions:', result);

        // Update local state immediately with accurate counts from Functions
        setFollowStats(prev => ({
          ...prev,
          followingCount: result.followingCount || Math.max(0, prev.followingCount - 1),
          followedUsers: prev.followedUsers.filter(id => id !== targetUserId),
          lastUpdated: new Date()
        }));

        // Broadcast unfollow event for real-time updates across all components
        socialEventBroadcaster.broadcastUnfollow(user.uid, targetUserId);

        console.log('✅ Local unfollow state updated immediately and event broadcasted');

        void hapticLight();

      } else {
        throw new Error(result.message || 'Unfollow operation failed');
      }

    } catch (error) {
      console.error('❌ Atomic unfollow operation failed:', error);

      // Log error but don't show intrusive alerts for network issues
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.warn('Unfollow operation failed:', errorMessage);
      throw error; // Re-throw for component error handling
    }
  }, [user?.uid]);

  // 🚀 Enhanced isFollowing check using Functions when available
  const isFollowing = useCallback(async (targetUserId: string): Promise<boolean> => {
    if (!user || targetUserId === user.uid) return false;

    if (shouldUseFunctionsAPI()) {
      try {
        console.log('🚀 Checking follow status via Functions API for:', targetUserId);
        const status: FollowStatusResult = await getFollowStatus(targetUserId);
        return status.isFollowing;
      } catch (error) {
        console.warn('Functions follow status check failed, falling back to Firestore:', error);
        // Fallback to legacy method
      }
    }

    try {
      return await socialService.isFollowing(user.uid, targetUserId);
    } catch (error) {
      console.error('Error checking follow status:', error);
      return false;
    }
  }, [user?.uid]);

  const getFollowingActivity = useCallback(async () => {
    if (!user || followStats.followedUsers.length === 0) {
      setFollowingActivity([]);
      return;
    }

    try {
      const activity = await socialService.getFollowingActivity(followStats.followedUsers, 30);
      setFollowingActivity(activity);
    } catch (error) {
      console.error('Error loading following activity:', error);
      setError('Failed to load activity feed');
    }
  }, [user?.uid, followStats.followedUsers]);

  const getUserFollowers = useCallback(async (userId: string): Promise<string[]> => {
    try {
      return await privacyService.getFollowersForProfile(userId, user?.uid);
    } catch (error) {
      console.error('Error getting user followers:', error);
      return [];
    }
  }, [user?.uid]);

  const getUserFollowing = useCallback(async (userId: string): Promise<string[]> => {
    try {
      return await privacyService.getFollowingForProfile(userId, user?.uid);
    } catch (error) {
      console.error('Error getting user following:', error);
      return [];
    }
  }, [user?.uid]);

  const isUserFollowed = useCallback((userId: string): boolean => {
    if (!user || userId === user.uid) return false;
    return followStats.followedUsers.includes(userId);
  }, [user?.uid, followStats.followedUsers]);

  // 🚀 NEW: Enhanced follow state refresh using Functions when available
  const refreshUserFollowState = useCallback(async (userId: string): Promise<boolean> => {
    if (!user || userId === user.uid) return false;

    if (shouldUseFunctionsAPI()) {
      try {
        console.log('🚀 Refreshing follow state via Functions API for:', userId);
        const status: FollowStatusResult = await getFollowStatus(userId);

        // Update local state to reflect actual follow status
        setFollowStats(prev => {
          const updatedFollowedUsers = status.isFollowing
            ? Array.from(new Set([...prev.followedUsers, userId]))
            : prev.followedUsers.filter(id => id !== userId);

          return {
            ...prev,
            followedUsers: updatedFollowedUsers,
            followingCount: updatedFollowedUsers.length
          };
        });

        return status.isFollowing;
      } catch (error) {
        console.warn('Functions follow state refresh failed, falling back to Firestore:', error);
      }
    }

    try {
      const isFollowingUser = await socialService.isFollowing(user.uid, userId);

      // Update local state to reflect actual follow status
      setFollowStats(prev => {
        const updatedFollowedUsers = isFollowingUser
          ? Array.from(new Set([...prev.followedUsers, userId]))
          : prev.followedUsers.filter(id => id !== userId);

        return {
          ...prev,
          followedUsers: updatedFollowedUsers,
          followingCount: updatedFollowedUsers.length
        };
      });

      return isFollowingUser;
    } catch (error) {
      console.error('Error refreshing user follow state:', error);
      return false;
    }
  }, [user?.uid]);

  const sendFollowRequest = useCallback(async (targetUserId: string) => {
    if (!user) throw new Error('Must be logged in');
    await apiSendFollowRequest(targetUserId, user.uid);
    setPendingRequests(prev => new Set([...prev, targetUserId]));
    void hapticLight();
  }, [user?.uid]);

  const cancelFollowRequest = useCallback(async (targetUserId: string) => {
    if (!user) throw new Error('Must be logged in');
    await apiCancelFollowRequest(targetUserId, user.uid);
    setPendingRequests(prev => {
      const next = new Set(prev);
      next.delete(targetUserId);
      return next;
    });
  }, [user?.uid]);

  const acceptFollowRequest = useCallback(async (requesterUserId: string) => {
    if (!user) throw new Error('Must be logged in');
    await apiAcceptFollowRequest(requesterUserId, user.uid);
    // Update our followers list — requesterUserId now follows us.
    // Do NOT broadcast a follow event: the event listener would incorrectly
    // add the requester to our followedUsers (making UI think we follow them).
    setFollowStats(prev => ({
      ...prev,
      followersCount: prev.followersCount + 1,
      followers: Array.from(new Set([...prev.followers, requesterUserId])),
      lastUpdated: new Date()
    }));
  }, [user?.uid]);

  const declineFollowRequest = useCallback(async (requesterUserId: string) => {
    if (!user) throw new Error('Must be logged in');
    await apiDeclineFollowRequest(requesterUserId, user.uid);
  }, [user?.uid]);

  const isRequestPending = useCallback((targetUserId: string): boolean => {
    return pendingRequests.has(targetUserId);
  }, [pendingRequests]);

  // Hydrate pending requests on mount
  useEffect(() => {
    if (!user) { setPendingRequests(new Set()); return; }
    socialService.getPendingFollowRequests(user.uid).then(() => {
      // getPendingFollowRequests returns incoming requests (to this user).
      // For outgoing we'd need a separate query — skip for now, hasFollowRequest
      // is checked per-profile when the OtherUserProfile mounts.
    }).catch(() => {});
  }, [user?.uid]);

  const value: UserFollowContextType = {
    followStats,
    followingActivity,
    isLoading,
    error,
    pendingRequests,
    followUser,
    unfollowUser,
    isFollowing,
    sendFollowRequest,
    cancelFollowRequest,
    acceptFollowRequest,
    declineFollowRequest,
    isRequestPending,
    refreshFollowData,
    getFollowingActivity,
    getUserFollowers,
    getUserFollowing,
    isUserFollowed,
    refreshUserFollowState
  };

  return (
    <UserFollowContext.Provider value={value}>
      {children}
    </UserFollowContext.Provider>
  );
};

export const useUserFollow = (): UserFollowContextType => {
  const context = useContext(UserFollowContext);
  if (context === undefined) {
    throw new Error('useUserFollow must be used within a UserFollowProvider');
  }
  return context;
};

export default UserFollowContext; 